import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Loader2, Package, XCircle } from 'lucide-react';
import { isTerminalStatus } from '@shipswift/shared';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';

/**
 * Watch a run as the server works through it.
 *
 * Subscribes to server-sent events rather than polling, and — the point of
 * moving runs server-side in the first place — closing this page does not stop
 * anything. Reopening it picks the run back up wherever it has got to.
 */
export default function BatchProgressPanel({ batchId, orderId, onStartNew }) {
  const [progress, setProgress] = useState(null);
  const [connectionLost, setConnectionLost] = useState(false);

  useEffect(() => {
    const source = new EventSource(`/api/batches/${batchId}/events`);

    source.onmessage = (event) => {
      setConnectionLost(false);
      setProgress(JSON.parse(event.data));
    };

    source.addEventListener('done', () => source.close());

    // EventSource reconnects on its own; this only surfaces the gap so nobody
    // reads a frozen number as a stalled run.
    source.onerror = () => setConnectionLost(true);

    return () => source.close();
  }, [batchId]);

  if (!progress) {
    return (
      <Card className="border-none shadow-lg">
        <CardContent className="p-12 flex items-center justify-center gap-3 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin" />
          Starting…
        </CardContent>
      </Card>
    );
  }

  const done = progress.succeededItems + progress.failedItems + progress.skippedItems;
  const percent = progress.totalItems > 0 ? Math.round((done / progress.totalItems) * 100) : 0;
  const finished = isTerminalStatus(progress.status);
  const running = progress.status === 'PURCHASING';

  return (
    <Card className="border-none shadow-lg">
      <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50">
        <CardTitle className="flex items-center gap-2">
          {running ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
              Buying labels…
            </>
          ) : progress.status === 'COMPLETED' ? (
            <>
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              Run complete
            </>
          ) : progress.status === 'CANCELLED' ? (
            <>
              <XCircle className="w-5 h-5 text-slate-500" />
              Run cancelled
            </>
          ) : (
            <>
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Run finished with problems
            </>
          )}
        </CardTitle>
      </CardHeader>

      <CardContent className="p-6 space-y-5">
        {running && (
          <Alert className="border-blue-200 bg-blue-50">
            <AlertDescription className="text-blue-800">
              This is running on the server. You can close this page or shut the browser — the run
              carries on, and reopening the order shows where it got to.
            </AlertDescription>
          </Alert>
        )}

        {connectionLost && (
          <Alert className="border-slate-200 bg-slate-50">
            <AlertDescription className="text-slate-700">
              Lost contact with the server — reconnecting. The run itself is unaffected.
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="font-medium text-slate-700">
              {done} of {progress.totalItems}
            </span>
            <span className="text-slate-500">{percent}%</span>
          </div>
          <Progress value={percent} className="h-3" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
          <Stat label="Shipped" value={progress.succeededItems} tone="green" />
          <Stat label="Failed" value={progress.failedItems} tone="red" />
          <Stat label="Skipped" value={progress.skippedItems} tone="slate" />
          <Stat label="Spent" value={`$${progress.totalCost.toFixed(2)}`} tone="blue" />
        </div>

        {progress.lastError && (
          <Alert className="border-red-200 bg-red-50">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">{progress.lastError}</AlertDescription>
          </Alert>
        )}

        {finished && (
          <div className="flex flex-wrap gap-2 pt-2">
            <Button asChild className="bg-blue-600 hover:bg-blue-700 gap-2">
              <Link to={`/orders/${orderId}`}>
                <Package className="w-4 h-4" />
                Open the order
              </Link>
            </Button>
            <Button variant="outline" onClick={onStartNew}>
              Start another upload
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ label, value, tone }) {
  const tones = {
    green: 'bg-green-50 border-green-200 text-green-800',
    red: 'bg-red-50 border-red-200 text-red-800',
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
  };

  return (
    <div className={`rounded-lg border p-3 ${tones[tone]}`}>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs uppercase tracking-wide">{label}</p>
    </div>
  );
}
